import { isZipHeader, normalizeZip } from './zipCodes';

type LabelDef = { validValues: string[]; label: string };

export const uniqueHeaders: LabelDef[] = [
  {
    label: 'uuid',
    validValues: ['uuid', 'unique_id', 'uniqueid', 'unique id', 'id'],
  },
  {
    label: 'hh_id',
    validValues: [
      'hh_id',
      'household_id',
      'householdid',
      'household id',
      'aiq_hhid',
      'hhid',
    ],
  },
  {
    label: 'ind_id',
    validValues: [
      'ind_id',
      'individual_id',
      'individualid',
      'individual id',
      'aiq_indid',
      'indid',
    ],
  },
  {
    label: 'email',
    validValues: ['email', 'email_address', 'emailaddress', 'email address'],
  },
  {
    label: 'phone_number',
    validValues: ['phone_number', 'phonenumber', 'phone number', 'phone'],
  },
  {
    label: 'area_code',
    validValues: ['area_code', 'areacode', 'area code'],
  },
];

export const uniqueHeaderCombinationsLabels = [
  ['uuid'],
  ['hh_id', 'ind_id'],
  ['email'],
  ['phone_number', 'area_code'],
];

export const uniqueHeaderCombinationsDefs = [
  [uniqueHeaders[0]],
  [uniqueHeaders[1], uniqueHeaders[2]],
  [uniqueHeaders[3]],
  [uniqueHeaders[4], uniqueHeaders[5]],
];

export const fuzzyMatchHeaders: LabelDef[] = [
  {
    label: 'first_name',
    validValues: ['first_name', 'firstname', 'first name', 'fname'],
  },
  {
    label: 'last_name',
    validValues: ['last_name', 'lastname', 'last name', 'lname'],
  },
  {
    label: 'street_address',
    validValues: [
      'primary_address',
      'primaryaddress',
      'address',
      'address1',
      'primary address',
      'street_address',
      'street address',
      'streetaddress',
    ],
  },
  { label: 'city', validValues: ['city'] },
  { label: 'state', validValues: ['state'] },
  {
    label: 'zip',
    validValues: ['zip', 'zipcode', 'zip5', 'zip code', 'zip_code'],
  },
];

export const allValidHeaderLabels = [
  ...uniqueHeaderCombinationsLabels.flatMap((label) => label),
  ...fuzzyMatchHeaders.map((labelDef) => labelDef.label),
];

function findLabelDefByValidValueOrNull(value: string, labelDefs: LabelDef[]) {
  return labelDefs.find((labelDef) =>
    labelDef.validValues.some((validValue) => validValue === value)
  );
}

/** One entry from Papa Parse's `results.errors`. */
export type CsvParseError = {
  type?: string;
  code?: string;
  message?: string;
  row?: number;
};

/**
 * Appended to every rejection, per the ticket: point at the rules. "Field
 * requirements" is the wording on the instructions panel's own toggle, so the
 * sentence names something the user can actually see. Deliberately no
 * "above"/"below" — that depends on where the panel sits relative to the
 * error text.
 */
export const FILE_FORMAT_HINT =
  "Check your file's format against the field requirements, then try again.";

/**
 * Characters that mark a header row as "one column that should have been
 * several". Papa guesses comma, tab, pipe and semicolon, so detection only
 * really fails on a separator outside that set — a colon, say — but its own
 * candidates are listed too so this stays correct if that guess list ever
 * narrows.
 *
 * Space is deliberately absent: "household id" is a supported header alias,
 * and a single-column file using it gets a far better message from
 * `validateHeaders` ("missing the aiq_indid column").
 */
const UNDETECTED_SEPARATORS = [';', ':', '|', '\t'];

/**
 * Control characters no text CSV contains — the signature of a binary file
 * that was renamed rather than exported. Tab, newline and carriage return
 * are excluded on purpose; they are legal CSV content.
 */
const BINARY_CHARS = /[\u0000-\u0008\u000e-\u001f]/;

/**
 * ATAD-135. Papa Parse's diagnostics are written for whoever is calling the
 * parser, not for whoever picked the file, and they used to be rendered
 * verbatim: someone who uploaded a list of zip codes was told "Unable to
 * auto-detect delimiting character; defaulted to ','", which names neither
 * what is wrong with the file nor what to do about it.
 *
 * Two things are wrong with passing those through:
 *
 *   1. Most of them are not the user's problem. A one-column file has no
 *      delimiter to detect, so `UndetectableDelimiter` fires on every such
 *      file even though Papa parsed it perfectly well. Reporting it aborted
 *      validation before `validateHeaders` could say the useful thing — that
 *      the file's columns aren't the ones we accept.
 *   2. The rest are diagnosable, but only if translated.
 *
 * So: return a message only for the diagnostics a user can act on, and
 * otherwise null, leaving header validation to speak.
 *
 * @returns the sentence to show, without the trailing FILE_FORMAT_HINT that
 *   `validateParsedFile` appends, or null if nothing here concerns the user
 */
export function describeParseError(
  errors: CsvParseError[] | undefined,
  fields: (string | null)[] | undefined
): string | null {
  if (!errors?.length) {
    return null;
  }

  const has = (type: string) => errors.some((err) => err.type === type);
  const rowOf = (type: string) =>
    errors.find((err) => err.type === type && typeof err.row === 'number')?.row;

  // An unterminated quote misreads every row after it, so it outranks the
  // field-count errors it causes.
  if (has('Quotes')) {
    const row = rowOf('Quotes');
    return `Your file has a quotation mark that is never closed${
      row === undefined ? '' : ` (around row ${row + 1})`
    }, so the rest of the file can't be read.`;
  }

  if (has('FieldMismatch')) {
    const row = rowOf('FieldMismatch');
    return `Your file has rows with a different number of columns than the header row${
      row === undefined ? '' : ` (starting at row ${row + 1})`
    }. Every row needs exactly two values: aiq_hhid and aiq_indid.`;
  }

  if (has('Delimiter')) {
    const headers = (fields ?? []).filter((h): h is string => Boolean(h));

    if (headers.length === 0) {
      return 'Your file appears to be empty.';
    }

    if (headers.some((header) => BINARY_CHARS.test(header))) {
      return "This doesn't look like a CSV file. If you exported it from Excel, re-save it as CSV (comma-separated) rather than renaming the file.";
    }

    if (
      headers.length === 1 &&
      UNDETECTED_SEPARATORS.some((sep) => headers[0].includes(sep))
    ) {
      return "We couldn't tell how your file separates its columns. Save it as comma-separated (CSV) with exactly two columns: aiq_hhid and aiq_indid.";
    }

    // Papa produced usable headers — the delimiter warning says nothing the
    // user needs. Whatever is actually wrong (the zip-code file, say) is a
    // header problem, and `validateHeaders` names the offending columns.
    return null;
  }

  return "We couldn't read this file.";
}

export function validateHeaders(headers: (string | null)[]) {
  // The matching ETL currently supports exact match on (aiq_hhid, aiq_indid)
  // only. A file is accepted iff its headers are exactly those two columns
  // (variants are permitted — see the hh_id / ind_id alias lists above, which
  // mirror etl/aiq-process_user_file_v2.py's column_cleanse aliases). Any
  // additional column is rejected.
  //
  // To re-enable uuid / email / phone / fuzzy support, update this function
  // AND the matching ETL together (the ETL needs a richer index — see
  // docs/opensearch-migration-plan.md §5).

  const hhIdDef = uniqueHeaders.find((h) => h.label === 'hh_id');
  const indIdDef = uniqueHeaders.find((h) => h.label === 'ind_id');
  if (!hhIdDef || !indIdDef) {
    // Defensive — would only fire if uniqueHeaders was mutated elsewhere.
    throw new Error('Internal config error: hh_id / ind_id aliases missing.');
  }

  const normalized = headers
    .filter((header): header is string => Boolean(header))
    .map((h) => h.toLowerCase().trim());

  if (normalized.length === 0) {
    throw new Error(
      'Your file has no headers. Please upload a CSV with exactly two columns: aiq_hhid and aiq_indid.'
    );
  }

  // Canonicalize each header against the hh_id / ind_id alias sets.
  const canonical = normalized.map((h) => {
    if (hhIdDef.validValues.includes(h)) return 'hh_id';
    if (indIdDef.validValues.includes(h)) return 'ind_id';
    return h; // unknown — surfaced below
  });

  const unknown = canonical.filter((c) => c !== 'hh_id' && c !== 'ind_id');
  if (unknown.length > 0) {
    throw new Error(
      `Your file contains unsupported columns: ${unknown.join(', ')}. ` +
        `Only aiq_hhid and aiq_indid are accepted right now.`
    );
  }

  const hhCount = canonical.filter((c) => c === 'hh_id').length;
  const indCount = canonical.filter((c) => c === 'ind_id').length;

  if (hhCount === 0 && indCount === 0) {
    throw new Error(
      'Your file is missing both aiq_hhid and aiq_indid columns.'
    );
  }
  if (hhCount === 0) {
    throw new Error('Your file is missing the aiq_hhid column.');
  }
  if (indCount === 0) {
    throw new Error('Your file is missing the aiq_indid column.');
  }
  if (hhCount > 1) {
    throw new Error(
      'Your file has more than one column mapping to aiq_hhid. Keep only one.'
    );
  }
  if (indCount > 1) {
    throw new Error(
      'Your file has more than one column mapping to aiq_indid. Keep only one.'
    );
  }

  // Defensive: hhCount === 1 && indCount === 1 implies canonical.length === 2.
  if (canonical.length !== 2) {
    throw new Error(
      'Your file must contain exactly two columns: aiq_hhid and aiq_indid.'
    );
  }
}

/**
 * The whole accept/reject decision for a parsed file, in one place: what the
 * parser complained about, then whether the columns are the ones the matching
 * ETL supports. Returns null when the file is good, or the message to show —
 * always closing with the pointer at the on-screen requirements that ATAD-135
 * asked for.
 *
 * The component calls only this, so the two halves can't drift apart or be
 * assembled differently by a future caller.
 */
/**
 * ATAD-136: shown when an Audience IDs upload is really a ZIP code list and
 * the modal offers a ZIP codes option to switch to.
 */
export const ZIP_FILE_TYPE_HINT = 'Uploading ZIP codes? Choose "ZIP codes" as the file type.';

/** True when every header is a ZIP alias or itself a ZIP (a headerless ZIP file). */
function looksLikeZipFile(fields: (string | null)[] | undefined) {
  const headers = (fields ?? []).filter((header): header is string => Boolean(header));
  return (
    headers.length > 0 &&
    headers.every((header) => isZipHeader(header) || normalizeZip(header) !== null)
  );
}

export function validateParsedFile(
  results: {
    errors?: CsvParseError[];
    meta?: { fields?: (string | null)[] };
  },
  { zipUploadAvailable = false }: { zipUploadAvailable?: boolean } = {}
): string | null {
  const fields = results.meta?.fields;
  const zipHint =
    zipUploadAvailable && looksLikeZipFile(fields) ? ` ${ZIP_FILE_TYPE_HINT}` : '';
  const parseProblem = describeParseError(results.errors, fields);

  if (parseProblem) {
    return `${parseProblem}${zipHint} ${FILE_FORMAT_HINT}`;
  }

  try {
    validateHeaders((fields ?? []).filter((header) => !!header));
    return null;
  } catch (err) {
    return `${(err as Error).message}${zipHint} ${FILE_FORMAT_HINT}`;
  }
}

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
